import React from "react";
import { Input, Card } from "components/ui";
import { RichTextEditor } from "components/shared/RichTextEditor";
import { PhoneIcon, VideoCameraIcon, MapPinIcon } from "@heroicons/react/24/outline";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import ReactSelect, { components as RSComponents } from "react-select";
import { ChevronDownIcon } from "@heroicons/react/20/solid";
import enLocale from "react-phone-number-input/locale/en.json";

const CountryOption = (props) => {
    const Icon = props.data.icon;
    return (
        <RSComponents.Option {...props}>
            <div className="flex items-center gap-2">
                {Icon && props.data.value && props.data.value !== 'ZZ' ? (
                    <div className="w-5 h-4 flex-shrink-0">
                        <Icon country={props.data.value} label={props.data.label} />
                    </div>
                ) : (
                    <div className="w-5 h-4 flex-shrink-0 bg-gray-200 dark:bg-gray-700 rounded-sm" />
                )}
                <span className="text-sm truncate">{props.data.label}</span>
            </div>
        </RSComponents.Option>
    );
};

const CountrySingleValue = (props) => {
    const Icon = props.data.icon;
    return (
        <RSComponents.SingleValue {...props}>
            <div className="flex items-center">
                {Icon && props.data.value && props.data.value !== 'ZZ' ? (
                    <Icon country={props.data.value} label={props.data.label} />
                ) : (
                    <div className="w-[1.25rem] h-[0.9375rem] bg-gray-200 dark:bg-gray-700 rounded-sm" />
                )}
            </div>
        </RSComponents.SingleValue>
    );
};

const CountryDropdownIndicator = (props) => {
    return (
        <RSComponents.DropdownIndicator {...props}>
            <ChevronDownIcon className="w-3 h-3 text-gray-500" />
        </RSComponents.DropdownIndicator>
    );
};

const CustomCountrySelect = ({ value, onChange, options, iconComponent: Icon }) => {
    const isDark = typeof document !== "undefined" && document.documentElement.classList.contains("dark");

    const customSelectStyles = {
        container: (provided) => ({
            ...provided,
            marginRight: '0px', 
        }),
        control: (provided) => ({
            ...provided,
            minHeight: "auto",
            height: "auto",
            border: "none",
            backgroundColor: "transparent",
            boxShadow: "none",
            cursor: "pointer",
            padding: "0",
            "&:hover": {
                backgroundColor: "transparent",
            },
        }),
        valueContainer: (provided) => ({
            ...provided,
            padding: "0",
            margin: "0",
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minWidth: 'auto',
        }),
        singleValue: (provided) => ({
            ...provided,
            margin: "0",
            padding: "0",
            display: "flex",
            alignItems: "center"
        }),
        input: (provided) => ({
            ...provided,
            margin: "0",
            padding: "0",
            "& input": {
                padding: "0 !important",
                margin: "0 !important",
                height: "auto !important",
                minHeight: "auto !important",
                fontSize: "14px !important",
                lineHeight: "1 !important"
            }
        }),
        indicatorsContainer: (provided) => ({
            ...provided,
            padding: "0",
            margin: "0",
        }),
        dropdownIndicator: (provided) => ({
            ...provided,
            padding: "0",
            paddingLeft: "2px",
            color: "#6b7280",
            "&:hover": {
                color: "#4b5563"
            }
        }),
        menu: (provided) => ({
            ...provided,
            zIndex: 9999,
            backgroundColor: isDark ? "#1e293b" : "#ffffff",
            border: isDark ? "1px solid #334155" : "1px solid #e5e7eb",
            borderRadius: "0.5rem",
            boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)",
            width: "300px",
            left: "-12px", // shift left to offset the px-3 padding of the container
        }),
        option: (provided, state) => ({
            ...provided,
            color: state.isFocused ? "#ffffff" : (isDark ? "#cbd5e1" : "#1f2937"),
            backgroundColor: state.isFocused
                ? "#2563eb"
                : state.isSelected
                    ? (isDark ? "#334155" : "#e5e7eb")
                    : "transparent",
            cursor: "pointer",
            fontSize: "14px",
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
        }),
        menuPortal: (provided) => ({
            ...provided,
            zIndex: 9999,
        }),
    };

    const selectOptions = options.map(opt => ({
        value: opt.value,
        label: opt.label,
        icon: Icon
    }));

    const selectedOption = selectOptions.find(opt => opt.value === value) || null;

    return (
        <ReactSelect
            options={selectOptions}
            value={selectedOption}
            onChange={(selected) => onChange(selected ? selected.value : undefined)}
            components={{
                Option: CountryOption,
                SingleValue: CountrySingleValue,
                DropdownIndicator: CountryDropdownIndicator,
                IndicatorSeparator: () => null
            }}
            styles={customSelectStyles}
            isSearchable={true}
            menuPortalTarget={typeof document !== "undefined" ? document.body : null}
            menuPosition="fixed"
            classNamePrefix="react-select"
        />
    );
};

/**
 * ActivityDetails Component
 * 
 * Handles specific input fields for non-communication activity types:
 * - Call: Optional phone number and rich text notes.
 * - Meeting: Meeting URL and rich text notes.
 * - Site Visit: Address and rich text notes.
 */
const getPlainTextLength = (htmlStr) => {
    if (!htmlStr) return 0;
    if (typeof window !== "undefined") {
        const doc = new DOMParser().parseFromString(htmlStr, "text/html");
        return (doc.body.textContent || "").length;
    }
    return htmlStr.replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").length;
};

export const ActivityDetails = ({
    type,
    phoneNumber,
    setPhoneNumber,
    meetingUrl,
    setMeetingUrl,
    address,
    setAddress,
    notes,
    setNotes,
    errors = {},
    setErrors = () => {},
}) => {
    const renderHeader = (icon, label, colorClass) => (
        <div className="flex items-center gap-3 mb-6">
            <div className={`p-2 rounded-lg bg-white dark:bg-dark-700 shadow-sm border border-gray-100 dark:border-dark-600 ${colorClass}`}>
                {React.createElement(icon, { className: "h-5 w-5" })}
            </div>
            <div>
                <h4 className="text-sm font-bold text-gray-900 dark:text-white leading-none">
                    {label}
                </h4>
                <p className="text-[10px] text-gray-500 dark:text-dark-400 mt-1 font-medium">Please provide the details below</p>
            </div>
        </div>
    );

    return (
        <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
            {/* Contextual Fields */}
            {type === 3 && (
                <Card className="relative overflow-hidden  from-indigo-50/50 to-blue-50/50 dark:from-indigo-950/10 dark:to-blue-950/10 border-2 border-indigo-100/50 dark:border-indigo-900/20 p-4  rounded-3xl">
                    {renderHeader(PhoneIcon, "Call Logistics", "text-emerald-600")}
                    <div className="relative z-10">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Phone Number <span className="text-red-500">*</span>
                        </label>
                        <div className={`relative rounded-xl border transition-all duration-200 bg-white/80 dark:bg-dark-800/80 backdrop-blur-sm px-3 h-11 flex items-center ${
                            errors?.phoneNumber
                                ? "border-red-300 focus-within:border-red-500 focus-within:ring-1 focus-within:ring-red-500"
                                : "border-gray-200 dark:border-dark-600 focus-within:border-primary-500 focus-within:ring-1 focus-within:ring-primary-500"
                        }`}>
                            <PhoneInput
                            countryCallingCodeEditable={false}
                                international
                                defaultCountry="AU"
                                countrySelectComponent={CustomCountrySelect}
                                value={phoneNumber || ""}
                                onChange={(val) => {
                                    setPhoneNumber(val || "");
                                    if (errors?.phoneNumber) {
                                        setErrors(prev => ({ ...prev, phoneNumber: null }));
                                    }
                                }}
                                className="w-full bg-transparent"
                                numberInputProps={{
                                    className: "w-full bg-transparent border-none focus:ring-0 p-0 text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none",
                                    required: true,
                                    placeholder: "Enter 10-digit number"
                                }}
                            />
                        </div>
                        {errors?.phoneNumber && (
                            <p className="text-xs text-red-500 mt-1">{errors.phoneNumber}</p>
                        )}
                        <div className="flex justify-between items-center mt-1.5 px-1">
                            <span className="text-[10px] text-gray-400">
                                {phoneNumber?.startsWith("+") ? "Enter a valid international number" : "Must be exactly 10 digits"}
                            </span>
                            <span className="text-[10px] text-gray-400 font-semibold">
                                {phoneNumber?.replace(/\D/g, "").length || 0} digits
                            </span>
                        </div>
                    </div>
                </Card>
            )}

            {type === 4 && (
                <Card className="relative overflow-hidden  from-indigo-50/50 to-blue-50/50 dark:from-indigo-950/10 dark:to-blue-950/10 border-2 border-indigo-100/50 dark:border-indigo-900/20 p-4  rounded-3xl">
                    {renderHeader(VideoCameraIcon, "Meeting Details", "text-indigo-600")}
                    <div className="relative z-10">
                        <Input
                            label="Meeting URL / Link"
                            required
                            maxLength={500}
                            error={errors?.meetingUrl}
                            placeholder="https://zoom.us/j/..."
                            value={meetingUrl || ""}
                            onChange={(e) => {
                                setMeetingUrl(e.target.value);
                                if (errors?.meetingUrl) {
                                    setErrors(prev => ({ ...prev, meetingUrl: null }));
                                }
                            }}
                            className="bg-white/80 dark:bg-dark-800/80 backdrop-blur-sm h-11"
                        />
                        <div className="flex justify-between items-center mt-1 px-1">
                            <span className="text-[10px] text-gray-400">Max 500 characters</span>
                            <span className="text-[10px] text-gray-400 font-semibold">{meetingUrl?.length || 0}/500</span>
                        </div>
                    </div>
                </Card>
            )}

            {type === 5 && (
                <Card className="relative overflow-hidden  from-indigo-50/50 to-blue-50/50 dark:from-indigo-950/10 dark:to-blue-950/10 border-2 border-indigo-100/50 dark:border-indigo-900/20 p-4  rounded-3xl">
                    {renderHeader(MapPinIcon, "Visit Location", "text-amber-600")}
                    <div className="relative z-10">
                        <Input
                            label="Full Job Address"
                            required
                            maxLength={500}
                            error={errors?.address}
                            placeholder="Enter full Job address"
                            value={address || ""}
                            onChange={(e) => {
                                setAddress(e.target.value);
                                if (errors?.address) {
                                    setErrors(prev => ({ ...prev, address: null }));
                                }
                            }}
                            className="bg-white/80 dark:bg-dark-800/80 backdrop-blur-sm h-11"
                        />
                        <div className="flex justify-between items-center mt-1 px-1">
                            <span className="text-[10px] text-gray-400">Max 500 characters</span>
                            <span className="text-[10px] text-gray-400 font-semibold">{address?.length || 0}/500</span>
                        </div>
                    </div>
                </Card>
            )}

            {/* Common Notes Section */}
            <div className="space-y-3 pt-0">
                <div className="flex items-center justify-between px-1">
                    <label className="text-sm font-semibold text-gray-800 dark:text-dark-100">
                        Detailed Activity Notes
                    </label>
                </div>
                <div className="overflow-hidde">
                    <RichTextEditor
                        value={notes || ""}
                        onChange={(val) => {
                            setNotes(val);
                            if (errors?.formattedLeadActivityNotes) {
                                setErrors(prev => ({ ...prev, formattedLeadActivityNotes: null }));
                            }
                        }}
                        placeholder="Add detailed notes, outcomes, or next steps about this activity..."
                        height="200px"
                        maxLength={500}
                        limitType="html"
                    />
                </div>
                <div className="flex justify-between items-center px-1">
                    {errors?.formattedLeadActivityNotes ? (
                        <span className="text-xs text-red-500 font-medium">{errors.formattedLeadActivityNotes}</span>
                    ) : (
                        <span className="text-[10px] text-gray-400">Max 500 characters</span>
                    )}
                    <span className={`text-[10px] font-semibold ${getPlainTextLength(notes) > 500 ? "text-red-500" : "text-gray-400"}`}>
                        {getPlainTextLength(notes)}/500
                    </span>
                </div>
            </div>
        </div>
    );
};
